import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Users, MapPin, Send, MessageSquare, FileText, Receipt, UserCheck, Bot, Share2, Sparkles, Zap, TrendingUp, ArrowRight } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { SEO } from '../components/SEO';
interface Service {
  id: string;
  icon: React.ElementType;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  category: string;
  categoryEn: string;
  status: 'active' | 'coming-soon';
  color: string;
  route?: string;
}
export function ServicesPage() {
  const {
    language
  } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const services: Service[] = [{
    id: 'whatsapp',
    icon: MessageSquare,
    title: 'مساعد WhatsApp الذكي',
    titleEn: 'AI WhatsApp Assistant',
    description: 'أتمتة محادثات واتساب مع ردود ذكية بالذكاء الاصطناعي',
    descriptionEn: 'Automate WhatsApp conversations with AI-powered smart replies',
    category: 'أتمتة واتساب',
    categoryEn: 'WhatsApp Automation',
    status: 'active',
    color: 'green',
    route: '/dashboard'
  }, {
    id: 'email-automation',
    icon: Mail,
    title: 'أتمتة البريد الإلكتروني + AI',
    titleEn: 'Email Automation + AI Reply',
    description: 'ربط الإيميل بالذكاء الاصطناعي للرد التلقائي وتصنيف الرسائل',
    descriptionEn: 'Connect email with AI for automatic replies and message classification',
    category: 'أتمتة البريد',
    categoryEn: 'Email Automation',
    status: 'coming-soon',
    color: 'blue'
  }, {
    id: 'lead-qualification',
    icon: Users,
    title: 'تصنيف العملاء المحتملين بالذكاء الاصطناعي',
    titleEn: 'AI Lead Qualification',
    description: 'فلترة وتصنيف العملاء تلقائيًا (Hot/Warm/Cold)',
    descriptionEn: 'Automatically filter and classify leads (Hot/Warm/Cold)',
    category: 'جذب العملاء',
    categoryEn: 'Lead Generation',
    status: 'coming-soon',
    color: 'purple'
  }, {
    id: 'google-maps-scraper',
    icon: MapPin,
    title: 'جمع عملاء من Google Maps',
    titleEn: 'Google Maps Lead Generation',
    description: 'استخراج بيانات الشركات من Google Maps تلقائيًا',
    descriptionEn: 'Extract business data from Google Maps automatically',
    category: 'جذب العملاء',
    categoryEn: 'Lead Generation',
    status: 'coming-soon',
    color: 'red'
  }, {
    id: 'cold-email',
    icon: Send,
    title: 'حملات البريد الإلكتروني الذكية',
    titleEn: 'Cold Email Automation',
    description: 'إرسال إيميلات مخصصة بالذكاء الاصطناعي مع متابعة تلقائية',
    descriptionEn: 'Send personalized AI emails with automatic follow-ups',
    category: 'أتمتة المبيعات',
    categoryEn: 'Sales Automation',
    status: 'coming-soon',
    color: 'indigo'
  }, {
    id: 'whatsapp-outreach',
    icon: MessageSquare,
    title: 'حملات واتساب الذكية',
    titleEn: 'AI WhatsApp Outreach',
    description: 'إطلاق حملات واتساب مخصصة مع ردود ذكية',
    descriptionEn: 'Launch personalized WhatsApp campaigns with smart replies',
    category: 'أتمتة واتساب',
    categoryEn: 'WhatsApp Automation',
    status: 'coming-soon',
    color: 'green'
  }, {
    id: 'forms-automation',
    icon: FileText,
    title: 'أتمتة النماذج → CRM',
    titleEn: 'Website Forms → CRM Automation',
    description: 'تحويل نماذج الموقع لعملية ذكية كاملة',
    descriptionEn: 'Transform website forms into complete smart processes',
    category: 'أتمتة الأعمال',
    categoryEn: 'Business Automation',
    status: 'coming-soon',
    color: 'orange'
  }, {
    id: 'invoice-processing',
    icon: Receipt,
    title: 'معالجة الفواتير بالذكاء الاصطناعي',
    titleEn: 'AI Invoice Processing',
    description: 'تحويل الفواتير والمستندات لبيانات منظمة تلقائيًا',
    descriptionEn: 'Convert invoices and documents to organized data automatically',
    category: 'أتمتة الأعمال',
    categoryEn: 'Business Automation',
    status: 'coming-soon',
    color: 'yellow'
  }, {
    id: 'customer-followup',
    icon: UserCheck,
    title: 'متابعة العملاء الذكية',
    titleEn: 'AI Customer Follow-up',
    description: 'متابعة تلقائية للعملاء بعد المكالمات والعروض',
    descriptionEn: 'Automatic customer follow-up after calls and quotes',
    category: 'دعم العملاء',
    categoryEn: 'Customer Support',
    status: 'coming-soon',
    color: 'teal'
  }, {
    id: 'internal-assistant',
    icon: Bot,
    title: 'المساعد الداخلي للشركات',
    titleEn: 'AI Internal Assistant',
    description: 'مساعد ذكي للإجابة على أسئلة الموظفين',
    descriptionEn: 'Smart assistant to answer employee questions',
    category: 'أدوات الذكاء الاصطناعي',
    categoryEn: 'AI Business Tools',
    status: 'coming-soon',
    color: 'violet'
  }, {
    id: 'social-dm',
    icon: Share2,
    title: 'أتمتة رسائل السوشيال ميديا',
    titleEn: 'Social Media DM Automation',
    description: 'رد تلقائي على Instagram و Facebook Messenger',
    descriptionEn: 'Automatic replies on Instagram and Facebook Messenger',
    category: 'دعم العملاء',
    categoryEn: 'Customer Support',
    status: 'coming-soon',
    color: 'pink'
  }];
  const premiumServices = [{
    icon: Sparkles,
    title: isRTL ? 'تصميم أتمتة مخصصة' : 'Custom Automation Design',
    description: isRTL ? 'تصميم نظام كامل حسب احتياجات عملك' : 'Design complete system based on your business needs'
  }, {
    icon: Zap,
    title: isRTL ? 'تدريب الذكاء الاصطناعي' : 'AI Training & Prompt Engineering',
    description: isRTL ? 'تدريب AI على بيانات شركتك الخاصة' : "Train AI on your company's specific data"
  }, {
    icon: TrendingUp,
    title: isRTL ? 'نظام مبيعات متكامل' : 'Full Sales Automation System',
    description: isRTL ? 'من العميل المحتمل إلى البيع والمتابعة' : 'From lead to sale to follow-up'
  }];
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-700 border-green-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    teal: 'bg-teal-100 text-teal-700 border-teal-200',
    violet: 'bg-violet-100 text-violet-700 border-violet-200',
    pink: 'bg-pink-100 text-pink-700 border-pink-200'
  };
  const handleServiceClick = (service: Service) => {
    if (service.status === 'active' && service.route) {
      navigate(service.route);
    }
  };
  return <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2937">
      <SEO title={isRTL ? 'خدماتنا - أتمتة ذكية لعملك' : 'Our Services - Smart Automation'} description={isRTL ? 'اكتشف خدمات Operines: أتمتة واتساب، أتمتة البريد الإلكتروني، جمع العملاء من Google Maps، حملات ذكية، وأكثر. حلول AI لكل الأعمال.' : 'Discover Operines services: WhatsApp automation, email automation, Google Maps lead generation, smart campaigns, and more. AI solutions for every business.'} keywords={isRTL ? 'خدمات أتمتة, واتساب, بريد إلكتروني, جمع عملاء, حملات تسويقية, ذكاء اصطناعي' : 'automation services, WhatsApp, email automation, lead generation, marketing campaigns, AI'} canonicalPath="/services" language={language as 'ar' | 'en'} data-id="element-2938" />
      <Navbar data-id="element-2939" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-2940">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-2941">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl font-bold text-gray-900 mb-4" data-id="element-2942">
            {isRTL ? 'خدماتنا' : 'Our Services'}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2943">
            {isRTL ? 'حلول أتمتة ذكية لتحويل عملك الرقمي' : 'Smart automation solutions to transform your digital business'}
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-id="element-2944">
          {services.map((service, index) => <motion.div key={service.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} onClick={() => handleServiceClick(service)} className={`bg-white rounded-2xl border-2 border-gray-200 p-6 transition-all ${service.status === 'active' ? 'hover:border-[#4f1964] hover:shadow-lg cursor-pointer' : 'opacity-75'}`} data-id="element-2945">
              <div className="flex items-start justify-between mb-4" data-id="element-2946">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[service.color]}`} data-id="element-2947">
                  <service.icon className="w-6 h-6" data-id="element-2948" />
                </div>
                {service.status === 'active' ? <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full border border-green-200" data-id="element-2949">
                    {isRTL ? 'متاح' : 'Active'}
                  </span> : <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200" data-id="element-2950">
                    {isRTL ? 'قريباً' : 'Coming Soon'}
                  </span>}
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2951">
                {isRTL ? service.title : service.titleEn}
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2952">
                {isRTL ? service.description : service.descriptionEn}
              </p>
              <div className="flex items-center justify-between" data-id="element-2953">
                <span className="text-xs text-gray-500" data-id="element-2954">
                  {isRTL ? service.category : service.categoryEn}
                </span>
                {service.status === 'active' && <ArrowRight className={`w-4 h-4 text-[#4f1964] ${isRTL ? 'rotate-180' : ''}`} data-id="element-2955" />}
              </div>
            </motion.div>)}
        </div>

        {/* Premium Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-gradient-to-br from-[#4f1964] to-[#6d28d9] rounded-3xl p-8 md:p-12 text-white" data-id="element-2956">
          <div className="text-center mb-8" data-id="element-2957">
            <h2 className="text-3xl font-bold mb-3" data-id="element-2958">
              {isRTL ? '🔥 خدمات مميزة' : '🔥 Premium Services'}
            </h2>
            <p className="text-purple-100 text-lg" data-id="element-2959">
              {isRTL ? 'حلول متقدمة للشركات الكبيرة' : 'Advanced solutions for large enterprises'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-2960">
            {premiumServices.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7 + index * 0.1
          }} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-2961">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4" data-id="element-2962">
                  <service.icon className="w-6 h-6 text-white" data-id="element-2963" />
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-2964">{service.title}</h3>
                <p className="text-purple-100 text-sm" data-id="element-2965">{service.description}</p>
              </motion.div>)}
          </div>

          <div className="text-center mt-8" data-id="element-2966">
            <Button variant="outline" size="lg" className="bg-white text-[#4f1964] hover:bg-gray-100 border-0" data-id="element-2967">
              {isRTL ? 'تواصل معنا' : 'Contact Us'}
            </Button>
          </div>
        </motion.div>
      </main>
    </div>;
}